(() => {
  const mobile = window.matchMedia('(max-width: 749px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const benefitsSelector = 'body:has([data-sonic-product-template]) .wt-product__feature-icons__list';
  const selector = `[data-swiper], [data-gallery], .sonic-faq-nav, ${benefitsSelector}`;
  const carousels = new Map();
  const pending = new Set();
  let proximity = null;

  function swiperPages(swiper) {
    if (swiper.params.loop) {
      const count = new Set([...swiper.slides].map((slide, index) =>
        slide.getAttribute('data-swiper-slide-index') ?? index)).size;
      return Array.from({ length: count }, (_, index) => ({ index, position: index }));
    }
    return swiper.snapGrid.map(position => ({
      position,
      index: swiper.slidesGrid.reduce((nearest, value, index, grid) =>
        Math.abs(value - position) < Math.abs(grid[nearest] - position) ? index : nearest, 0),
    }));
  }

  function nativePages(element, groupSize = 1) {
    const wrapper = element.querySelector('[data-swiper-container]') || element;
    const maximum = element.scrollWidth - element.clientWidth;
    if (maximum <= 1 || !/auto|scroll/.test(getComputedStyle(element).overflowX)) return [];
    const left = element.getBoundingClientRect().left;
    const pages = [];
    [...wrapper.children].forEach((slide, index) => {
      if (index % groupSize !== 0) return;
      if (slide.hidden || !slide.getBoundingClientRect().width) return;
      const bounds = slide.getBoundingClientRect();
      const centerOffset = getComputedStyle(slide).scrollSnapAlign === 'center'
        ? (element.clientWidth - bounds.width) / 2 : 0;
      const position = Math.max(0, Math.min(maximum,
        bounds.left - left + element.scrollLeft - centerOffset));
      if (!pages.some(page => Math.abs(page.position - position) < 2)) pages.push({ index, position });
    });
    return pages;
  }

  function attachBenefits(element) {
    const listeners = new AbortController();
    const previousTabindex = element.getAttribute('tabindex');
    let timer = null;
    let visible = false;
    let hovered = false;
    let interacted = false;

    function stop() {
      clearInterval(timer);
      timer = null;
    }

    function schedule() {
      stop();
      if (!visible || hovered || interacted || document.hidden || reducedMotion.matches
        || element.contains(document.activeElement) || nativePages(element, 2).length < 2) return;
      timer = setInterval(() => {
        const pages = nativePages(element, 2);
        if (pages.length < 2) return;
        const active = pages.reduce((nearest, page, index) =>
          Math.abs(page.position - element.scrollLeft) < Math.abs(pages[nearest].position - element.scrollLeft) ? index : nearest, 0);
        const next = (active + 1) % pages.length;
        element.scrollTo({ left: pages[next].position, behavior: next === 0 ? 'auto' : 'smooth' });
      }, 5000);
    }

    // Manual interaction takes over; never move a benefit away while it is being read.
    function takeOver() {
      interacted = true;
      stop();
    }

    element.setAttribute('tabindex', '0');
    const options = { signal: listeners.signal };
    element.addEventListener('pointerdown', takeOver, options);
    element.addEventListener('keydown', takeOver, options);
    element.addEventListener('wheel', takeOver, { ...options, passive: true });
    element.addEventListener('focusin', stop, options);
    element.addEventListener('focusout', schedule, options);
    element.addEventListener('mouseenter', () => { hovered = true; stop(); }, options);
    element.addEventListener('mouseleave', () => { hovered = false; schedule(); }, options);
    document.addEventListener('visibilitychange', schedule, options);
    reducedMotion.addEventListener('change', schedule, options);
    const visibility = new IntersectionObserver(entries => {
      visible = entries[0].intersectionRatio >= 0.5;
      schedule();
    }, { threshold: [0, 0.5] });
    visibility.observe(element);
    const sizes = new ResizeObserver(schedule);
    sizes.observe(element);
    carousels.set(element, {
      schedule,
      destroy() {
        stop();
        listeners.abort();
        visibility.disconnect();
        sizes.disconnect();
        if (previousTabindex === null) element.removeAttribute('tabindex');
        else element.setAttribute('tabindex', previousTabindex);
        element.scrollLeft = 0;
      },
    });
  }

  function attach(element) {
    if (carousels.has(element) || element.closest('[data-thumbs], .wt-multicol-slider__not-slider')) return;
    if (element.matches(benefitsSelector)) {
      attachBenefits(element);
      return;
    }
    const dots = document.createElement('div');
    dots.className = 'sonic-mobile-carousel-dots';
    dots.setAttribute('role', 'group');
    dots.setAttribute('aria-label', (window.sonicStrings?.carousel_pagination || "Carousel pagination"));
    dots.hidden = true;
    element.after(dots);
    element.setAttribute('data-sonic-mobile-carousel', '');
    let swiper = null;
    let pages = [];
    let frame = null;
    const events = ['slideChange', 'snapIndexChange', 'update', 'resize', 'breakpoint', 'destroy'];

    function schedule() {
      if (frame === null) frame = requestAnimationFrame(render);
    }

    function render() {
      frame = null;
      const current = element.swiper && !element.swiper.destroyed ? element.swiper : null;
      if (current !== swiper) {
        events.forEach(event => swiper?.off(event, schedule));
        swiper = current;
        events.forEach(event => swiper?.on(event, schedule));
      }
      if (!mobile.matches || !element.getBoundingClientRect().width) {
        dots.hidden = true;
        return;
      }
      pages = swiper && swiper.enabled && swiper.isHorizontal()
        ? swiperPages(swiper) : nativePages(element);
      dots.hidden = pages.length < 2;
      if (dots.hidden) return;
      if (dots.children.length !== pages.length) {
        dots.replaceChildren(...pages.map((page, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.setAttribute('aria-label', (window.sonicStrings?.go_to_slide || "Go to slide [index] of [total]").replaceAll("[index]", () => index + 1).replaceAll("[total]", () => pages.length));
          button.addEventListener('click', () => {
            const target = pages[index];
            if (!target) return;
            if (swiper) {
              const speed = reducedMotion.matches ? 0 : swiper.params.speed;
              if (swiper.params.loop) swiper.slideToLoop(target.index, speed);
              else swiper.slideTo(target.index, speed);
            } else {
              element.scrollTo({ left: target.position, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
            }
          });
          return button;
        }));
      }
      const active = swiper ? (swiper.params.loop ? swiper.realIndex : swiper.snapIndex)
        : pages.reduce((nearest, page, index) =>
          Math.abs(page.position - element.scrollLeft) < Math.abs(pages[nearest].position - element.scrollLeft) ? index : nearest, 0);
      [...dots.children].forEach((button, index) => {
        if (index === active) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
    }

    // Lazy sliders and variant galleries may initialise or replace slides after page load.
    const changes = new MutationObserver(schedule);
    changes.observe(element, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    const sizes = new ResizeObserver(schedule);
    sizes.observe(element);
    element.addEventListener('scroll', schedule, { passive: true });
    carousels.set(element, {
      schedule,
      destroy() {
        changes.disconnect();
        sizes.disconnect();
        element.removeEventListener('scroll', schedule);
        events.forEach(event => swiper?.off(event, schedule));
        if (frame !== null) cancelAnimationFrame(frame);
        dots.remove();
        element.removeAttribute('data-sonic-mobile-carousel');
      },
    });
    schedule();
  }

  function prepare(element) {
    if (carousels.has(element) || pending.has(element) || element.closest('[data-thumbs], .wt-multicol-slider__not-slider')) return;
    if (typeof IntersectionObserver === 'undefined') {
      attach(element);
      return;
    }
    if (!proximity) {
      proximity = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!mobile.matches || !entry.isIntersecting || !pending.has(entry.target)) return;
          pending.delete(entry.target);
          proximity.unobserve(entry.target);
          attach(entry.target);
        });
      }, { rootMargin: '400px 0px' });
    }
    pending.add(element);
    proximity.observe(element);
  }

  function initialize(root) {
    if (!mobile.matches) return;
    if (root.matches?.(selector)) prepare(root);
    root.querySelectorAll(selector).forEach(prepare);
  }

  mobile.addEventListener('change', () => {
    if (mobile.matches) {
      initialize(document);
    } else {
      proximity?.disconnect();
      proximity = null;
      pending.clear();
      carousels.forEach(carousel => carousel.destroy());
      carousels.clear();
    }
  });
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    pending.forEach(element => {
      if (event.target.contains(element)) {
        proximity?.unobserve(element);
        pending.delete(element);
      }
    });
    carousels.forEach((carousel, element) => {
      if (event.target.contains(element)) {
        carousel.destroy();
        carousels.delete(element);
      }
    });
  });
  initialize(document);
})();
